import type { DayPart, GuestCircle, Party, Person, WeddingData } from '@/mock/types'

export type GuestTab = 'list' | 'replies' | 'organization'
export type GuestFilter = 'all' | 'confirmed' | 'pending' | 'declined'

export const guestFilters: GuestFilter[] = ['all', 'confirmed', 'pending', 'declined']
export const filterLabels: Record<GuestFilter, string> = {
  all: 'Vše',
  confirmed: 'Přijdou',
  pending: 'Bez odpovědi',
  declined: 'Nepřijdou',
}

export const parseFilter = (value: unknown): GuestFilter =>
  guestFilters.includes(value as GuestFilter) ? (value as GuestFilter) : 'all'

export const circleOrder: GuestCircle[] = ['bride', 'groom', 'shared']
export const circleGroupLabels: Record<GuestCircle, string> = {
  bride: 'Hosté nevěsty',
  groom: 'Hosté ženicha',
  shared: 'Společní hosté',
}

export const isPending = (person: Person) =>
  person.attendance === 'invited' || person.attendance === 'waiting'

export const personName = (person: Person) =>
  person.plusOne && !person.firstName ? 'doprovod' : `${person.firstName} ${person.lastName}`.trim()

/** "Jana Nováková a Karel Novák", "Ondřej Svoboda + doprovod"; a set name wins. */
export function partyTitle(party: Party) {
  if (party.name) return party.name
  const named = party.persons.filter((person) => !(person.plusOne && !person.firstName))
  const companions = party.persons.length - named.length
  const names = named.map(personName)
  const joined =
    names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} a ${names.at(-1)}`
  return companions > 0 ? `${joined} + doprovod` : joined
}

/** The people line under a party that has its own name ("Kolegyně z práce"). */
export const partyPeople = (party: Party) => party.persons.map(personName).join(', ')

export function partyCounts(party: Party) {
  const confirmed = party.persons.filter((person) => person.attendance === 'confirmed').length
  const declined = party.persons.filter((person) => person.attendance === 'declined').length
  return {
    total: party.persons.length,
    confirmed,
    declined,
    pending: party.persons.length - confirmed - declined,
  }
}

export function matchesFilter(party: Party, filter: GuestFilter) {
  const counts = partyCounts(party)
  if (filter === 'confirmed') return counts.confirmed > 0
  if (filter === 'pending') return counts.pending > 0
  if (filter === 'declined') return counts.declined > 0
  return true
}

const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

/** Name search without needing Czech accents ("novakova" finds "Nováková"). */
export function matchesSearch(party: Party, query: string) {
  const needle = fold(query.trim())
  if (!needle) return true
  return fold(`${party.name ?? ''} ${partyPeople(party)}`).includes(needle)
}

export const hasNewReply = (party: Party, data: WeddingData) =>
  data.webReplies.some((reply) => reply.partyId === party.id && reply.status === 'new')

/** People-level numbers for the summary; a companion counts as a guest. */
export function guestTotals(data: WeddingData) {
  const persons = data.parties.flatMap((party) => party.persons)
  const confirmed = persons.filter((person) => person.attendance === 'confirmed').length
  const declined = persons.filter((person) => person.attendance === 'declined').length
  return {
    total: persons.length,
    confirmed,
    declined,
    pending: persons.length - confirmed - declined,
    pendingParties: data.parties.filter((party) => partyCounts(party).pending > 0).length,
    newReplies: data.webReplies.filter((reply) => reply.status === 'new').length,
  }
}

/** "Celý den" when the invitation covers every part of the day, otherwise the parts in day order. */
export function invitationLabel(parts: string[], dayParts: DayPart[]) {
  const covered = dayParts.filter((part) => parts.includes(part.id))
  if (covered.length === dayParts.length) return 'Celý den'
  return covered.map((part) => part.label).join(', ')
}
